import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { uuidv7 } from 'uuidv7'
import { parseCapture } from '../lib/capture.ts'
import { flushOfflineQueue, offlineQueue } from '../lib/hooks.ts'
import { isTauri } from '../lib/platform.ts'

export const Route = createFileRoute('/quick-capture')({ component: QuickCapture })

async function hide() {
  if (!isTauri) return
  const { invoke } = await import('@tauri-apps/api/core')
  await invoke('quick_capture_hide')
}

/**
 * Global Quick Capture window (⌥⇧Space on the Mac, §19.5 / §22): pill-shaped, Esc closes, ⌘↵ saves.
 * Always goes through the offline queue, so it works without network.
 */
function QuickCapture() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [saved, setSaved] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    document.documentElement.classList.add('quick-capture')
    const focus = () => ref.current?.focus()
    focus()
    window.addEventListener('focus', focus)
    return () => window.removeEventListener('focus', focus)
  }, [])

  const save = async (kind: 'note' | 'task') => {
    const parsed = parseCapture(text)
    if (!parsed.text) return
    const id = uuidv7()
    await offlineQueue.enqueue({ id, kind: 'capture', payload: { id, text: parsed.text, kind } })
    setText('')
    setSaved(true)
    setTimeout(() => setSaved(false), 1200)
    void flushOfflineQueue(queryClient)
    await hide()
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      void hide()
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void save(e.shiftKey ? 'task' : 'note')
    }
  }

  return (
    <div data-tauri-drag-region className="flex h-dvh items-center justify-center bg-transparent p-2">
      <div className="flex w-full items-start gap-3 rounded-[28px] border border-line-1 bg-card px-5 py-3 shadow-3">
        <span className="mt-1.5 size-2.5 shrink-0 rounded-pill bg-pop" aria-hidden />
        <textarea
          ref={ref}
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder={saved ? t('capture.saved') : t('capture.quickPlaceholder')}
          aria-label={t('capture.quickPlaceholder')}
          className="field-sizing-content max-h-40 min-w-0 flex-1 resize-none bg-transparent text-[16px] text-text-1 outline-none placeholder:text-meta"
        />
        <span className="mt-1 shrink-0 text-[11px] text-meta">⌘↵</span>
      </div>
    </div>
  )
}
